import * as THREE from 'three';
import { MathUtils } from '../utils/MathUtils.js';

/**
 * View orientation, subtle head-bob, and eye height on the render camera.
 */
export class PlayerCameraRig {
  /**
   * @param {{
   *   camera: import('../rendering/Camera.js').CameraSystem;
   *   config: import('../config.js').CONFIG['player'];
   * }} deps
   */
  constructor({ camera, config }) {
    this.camera = camera;
    this.config = config;

    this._displayYaw = 0;
    this._displayPitch = 0;
    this._bobPhase = 0;
    this._bobOffset = new THREE.Vector3();
    this._forward = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._worldUp = new THREE.Vector3(0, 1, 0);
    this._bobEuler = new THREE.Euler(0, 0, 0, 'YXZ');
    this._bobQuat = new THREE.Quaternion();
  }

  /** Hard-sync view angles (used on pointer-lock acquire). */
  syncLook(yaw, pitch) {
    this._displayYaw = MathUtils.wrapAngle(yaw);
    this._displayPitch = pitch;
  }

  /**
   * Yaw follows input immediately; pitch may smooth slightly for feel.
   * @returns {{ displayYaw: number; displayPitch: number }}
   */
  updateLook(yaw, pitch, delta) {
    this._displayYaw = MathUtils.wrapAngle(yaw);

    const pitchSmoothing = this.config.pitchSmoothing ?? 0;
    if (pitchSmoothing > 0) {
      const smooth = 1 - Math.exp(-pitchSmoothing * delta);
      this._displayPitch += (pitch - this._displayPitch) * smooth;
    } else {
      this._displayPitch = pitch;
    }

    return {
      displayYaw: this._displayYaw,
      displayPitch: this._displayPitch,
    };
  }

  /**
   * @param {{
   *   feet: THREE.Vector3;
   *   horizontalSpeed: number;
   *   onGround: boolean;
   *   delta: number;
   * }} state
   */
  apply(state) {
    const { feet, horizontalSpeed, onGround, delta } = state;
    const cam = this.camera.threeCamera;
    const { eyeHeight, headBob } = this.config;

    this._bobOffset.set(0, 0, 0);
    if (headBob.enabled && onGround && horizontalSpeed > headBob.minSpeed) {
      const speedFactor = MathUtils.clamp(
        horizontalSpeed / this.config.walkSpeed,
        0,
        1.6,
      );
      this._bobPhase += delta * headBob.frequency * speedFactor;
      const vertical =
        Math.sin(this._bobPhase * Math.PI * 2) *
        headBob.verticalAmplitude *
        speedFactor;
      const lateral =
        Math.cos(this._bobPhase * Math.PI) *
        headBob.lateralAmplitude *
        speedFactor;

      this._bobEuler.set(0, this._displayYaw, 0, 'YXZ');
      this._bobQuat.setFromEuler(this._bobEuler);
      this._forward.set(0, 0, -1).applyQuaternion(this._bobQuat);
      this._forward.y = 0;
      this._forward.normalize();
      this._right.crossVectors(this._forward, this._worldUp).normalize();
      this._bobOffset
        .copy(this._right)
        .multiplyScalar(lateral)
        .addScaledVector(this._worldUp, vertical);
    } else if (horizontalSpeed <= headBob.minSpeed) {
      this._bobPhase = 0;
    }

    cam.position
      .copy(feet)
      .addScaledVector(this._worldUp, eyeHeight)
      .add(this._bobOffset);

    cam.rotation.order = 'YXZ';
    cam.rotation.y = this._displayYaw;
    cam.rotation.x = this._displayPitch;
    cam.rotation.z = 0;
    cam.updateMatrixWorld(true);
  }
}
